import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { countryCodes } from '../utils/countryCodes'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'

const DROPDOWN_WIDTH = 320

const CountryCodePicker = ({ selectedCountry, onCountrySelect, error = false, embedded = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [menuStyle, setMenuStyle] = useState(null)
  const buttonRef = useRef(null)

  useBodyScrollLock(isOpen)

  const filteredCountries = countryCodes.filter(
    (country) =>
      country.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.code.includes(searchQuery),
  )

  useEffect(() => {
    if (!isOpen || !buttonRef.current) {
      setMenuStyle(null)
      return
    }

    const updatePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect()
      let left = rect.left
      if (left + DROPDOWN_WIDTH > window.innerWidth - 8) {
        left = window.innerWidth - DROPDOWN_WIDTH - 8
      }
      setMenuStyle({
        top: rect.bottom + 4,
        left: Math.max(8, left),
        width: DROPDOWN_WIDTH,
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  const handleSelect = (country) => {
    onCountrySelect(country)
    setIsOpen(false)
    setSearchQuery('')
  }

  const dropdown =
    isOpen && menuStyle
      ? createPortal(
          <>
            <div
              className="fixed inset-0 z-[10100]"
              onClick={() => {
                setIsOpen(false)
                setSearchQuery('')
              }}
              aria-hidden="true"
            />
            <div
              className="fixed z-[10101] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
              style={{ top: menuStyle.top, left: menuStyle.left, width: menuStyle.width }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-3 border-b border-gray-200">
                <div className="relative">
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoFocus
                  />
                </div>
              </div>
              <div
                className="overflow-y-auto max-h-64"
                style={{ scrollbarWidth: 'thin' }}
              >
                {filteredCountries.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">No countries found</div>
                ) : (
                  filteredCountries.map((country, index) => (
                    <button
                      key={`${country.code}-${country.country}-${index}`}
                      type="button"
                      onClick={() => handleSelect(country)}
                      className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition ${
                        selectedCountry?.code === country.code ? 'bg-primary/10' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 text-sm truncate">{country.country}</div>
                          <div className="text-sm text-gray-500">{country.code}</div>
                        </div>
                        {selectedCountry?.code === country.code ? (
                          <i className="fas fa-check text-primary shrink-0" />
                        ) : null}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </>,
          document.body,
        )
      : null

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`flex items-center gap-1.5 transition shrink-0 ${
          embedded
            ? `self-stretch px-2.5 inline-flex items-center border-0 bg-gray-50 hover:bg-gray-100 rounded-none ${error ? 'text-red-600' : 'text-gray-700'}`
            : `px-3 py-3 border rounded-l-lg bg-gray-50 hover:bg-gray-100 ${error ? 'border-red-500' : 'border-gray-300'}`
        }`}
        aria-label="Select country code"
        aria-expanded={isOpen}
      >
        <span className="text-sm font-medium whitespace-nowrap">
          {selectedCountry?.code || '+1'}
        </span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-[10px] text-gray-500`} />
      </button>
      {dropdown}
    </>
  )
}

export default CountryCodePicker
